/**
 * Formats location strings by adding proper spacing after commas and deduplicating if needed.
 * E.g., "Karnataka-Bagalkot,Kerala-Kollam,Karnataka-Bengaluru (Bangalore) Urban"
 * -> "Karnataka-Bagalkot, Kerala-Kollam, Karnataka-Bengaluru (Bangalore) Urban"
 */
export const formatLocation = (locationStr) => {
  if (!locationStr || typeof locationStr !== "string") return "";

  const rawParts = locationStr
    .split(/,\s*/)
    .map((s) => s.trim())
    .filter(Boolean);

  if (!rawParts.length) return "";

  const formattedParts = [];
  const seenDistricts = new Set();
  const seenFull = new Set();

  rawParts.forEach((part) => {
    // Check if part contains state-district with hyphen
    if (part.includes("-")) {
      const subParts = part.split(/\s*-\s*/);
      if (subParts.length === 2) {
        const [state, district] = subParts;
        const normalizedFull = `${state}-${district}`;
        const key = normalizedFull.toLowerCase();
        if (!seenFull.has(key)) {
          seenFull.add(key);
          seenDistricts.add(district.toLowerCase());
          formattedParts.push(normalizedFull);
        }
        return;
      }
    }

    const key = part.toLowerCase();
    if (seenDistricts.has(key) || seenFull.has(key)) {
      return;
    }

    seenFull.add(key);
    formattedParts.push(part);
  });

  return formattedParts.join(", ");
};

/**
 * Formats comma-separated strings with proper spacing.
 * E.g., "Children,Youth,Urban poor" -> "Children, Youth, Urban poor"
 */
export const formatCommaSeparated = (str) => {
  if (!str || typeof str !== "string") return "";
  return str
    .split(/,\s*(?=[A-Z])|,\s*/)
    .map((s) => s.trim())
    .filter(Boolean)
    .join(", ");
};

/**
 * Generates the cleanly formatted "What we do" summary paragraph for NPO profile.
 */
export const getSummaryDescription = (data) => {
  if (!data || typeof data !== "object") return "No description available.";

  const parts = [];

  if (data.registration_year) {
    parts.push(`We started our organisation in the year ${data.registration_year}`);
  } else {
    parts.push("We started our organisation");
  }

  const formattedSegments = formatCommaSeparated(data.segment_served);
  if (data.total_lives_changed || formattedSegments) {
    let servedText = "and since then served";
    if (data.total_lives_changed) {
      servedText += ` ${data.total_lives_changed} number(s) of`;
    }
    if (formattedSegments) {
      servedText += ` ${formattedSegments}`;
    }
    parts.push(servedText);
  }

  let causesList = [];
  if (Array.isArray(data.causes) && data.causes.length > 0) {
    causesList = data.causes
      .map((ele) => ele?.data)
      .filter(Boolean)
      .flatMap((str) => str.split(/,\s*(?=[A-Z])|,\s*/))
      .map((s) => s.trim())
      .filter(Boolean);
  } else if (typeof data.causes === "string" && data.causes) {
    causesList = data.causes
      .split(/,\s*(?=[A-Z])|,\s*/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (causesList.length > 0) {
    parts.push(`in the thematic area(s) of ${causesList.join(", ")}`);
  }

  const formattedLocation = formatLocation(data.where_we_work_district);
  const isPanIndia = data.pan_india === "Yes";

  if (formattedLocation || isPanIndia) {
    if (formattedLocation && isPanIndia) {
      parts.push(`in ${formattedLocation} and pan India`);
    } else if (formattedLocation) {
      parts.push(`in ${formattedLocation}`);
    } else if (isPanIndia) {
      parts.push("in pan India");
    }
  }

  if (data.key_indicator) {
    let indicatorText = `and use the key indicator(s): ${data.key_indicator}`;
    if (data.verified) {
      indicatorText += ` to track our progress, ${data.verified}`;
    }
    parts.push(indicatorText);
  }

  if (data.end_goal) {
    parts.push(`and hope to reach our goal of ${data.end_goal}`);
  }
  if (data.end_goal_year) {
    parts.push(`by around the year ${data.end_goal_year}`);
  }

  const fullSentence = parts.join(" ").trim();
  return fullSentence ? (fullSentence.endsWith(".") ? fullSentence : `${fullSentence}.`) : "No description available.";
};
